import { useMemo } from 'react'

import type { NavItemConfig } from '@/types/nav'
import { isRestrictedPath } from '@/lib/auth/roles'
import { useUser } from '@/hooks/use-user'
import {
	BETA_FEATURES_TAG,
	CABINET_HEALTH_TAG,
	FOOT_TRAFFIC_TAG,
	INTEGRATIONS_TAG,
	INVENTORY_TAG,
	MARKET_INSIGHTS_TAG,
	PASSCODE_TAG,
} from '@/components/dashboard/layout/workspaces/workspaces-context'

// Filter out nav items that the user is restricted from
export function useFilteredNavItems(navItems: NavItemConfig[] = []) {
	const { user } = useUser()

	return useMemo(() => {
		if (!user) return navItems

		const hasBetaAccess = user?.tags?.includes(BETA_FEATURES_TAG)
		const hasInventoryAccess = user?.tags?.includes(INVENTORY_TAG)
		const hasPasscodeAccess = user?.tags?.includes(PASSCODE_TAG)
		const hasMarketInsightsAccess = user?.tags?.includes(MARKET_INSIGHTS_TAG)
		const hasFootTrafficAccess = user?.tags?.includes(FOOT_TRAFFIC_TAG)
		const hasHealthAccess = user?.tags?.includes(CABINET_HEALTH_TAG)
		const hasIntegrationsAccess = user?.tags?.includes(INTEGRATIONS_TAG)
		const seenFeatures = user?.seen_features ?? []

		return navItems
			.map((group) => ({
				...group,
				items: (group.items ?? [])
					.filter((item) => {
						// Filter by beta access
						if (item.requiresBetaAccess && !hasBetaAccess) {
							return false
						}
						// Inventory: hide entirely when user lacks the tag
						if (item.requiresInventoryAccess && !hasInventoryAccess) {
							return false
						}
						// Passcode: hide entirely when user lacks the tag
						if (item.requiresPasscodeAccess && !hasPasscodeAccess) {
							return false
						}
						if (item.requiresMarketInsightsAccess && !hasMarketInsightsAccess) {
							return false
						}
						if (item.requiresFootTrafficAccess && !hasFootTrafficAccess) {
							return false
						}
						if (item.requiresHealthAccess && !hasHealthAccess) {
							return false
						}
						if (item.requiresIntegrationsAccess && !hasIntegrationsAccess) {
							return false
						}
						// Filter by restricted path
						return !isRestrictedPath(user, item.href || '')
					})
					.map((item) => {
						if (item.seenFeatureKey && seenFeatures.includes(item.seenFeatureKey)) {
							return { ...item, label: undefined }
						}
						return item
					}),
			}))
			.filter((group) => group.items.length > 0) // remove empty groups
	}, [user, navItems])
}
